/**
 * Login-screen Terms acceptance — per-install suppression (#3312).
 *
 * Apple App Review, Guideline 1.2 (reply 2026-09-03) requires that users AGREE
 * to terms. The checkbox lives on the login screen; this module decides whether
 * it still needs to be shown on this device.
 *
 * WHY CLIENT-LOCAL. The check cannot ask the server. `POST /auth/magic-link/request`
 * deliberately ends in a generic success ("Always return generic success
 * (prevent user enumeration)"), and asking whether an identifier has already
 * accepted, BEFORE authentication, is exactly the account-existence oracle that
 * comment prevents. So the server records the truth and this flag only reduces
 * friction.
 *
 * Owner requirement (#3312): seen exactly ONCE per device install.
 *
 * Feature doc: docs/canonical/features/terms-agreement-at-login.md
 */

const STORAGE_KEY = 'oro.terms.accepted';

/**
 * PRESENCE-BASED, NOT VERSION-MATCH — load-bearing.
 *
 * Editing a legal document must NOT re-prompt anyone: the owner requirement is
 * once per install, full stop. Re-acceptance on material change is #2996 scope 3
 * and is deliberately NOT implemented here. Turning it on later means comparing
 * a stored version against the server's, which first requires #2928's version
 * table and a way to surface the current version to the client.
 *
 * Returns false when storage is unreadable (private mode, disabled cookies,
 * quota errors), which fails toward SHOWING the box. Showing it once too often
 * is a shrug; skipping it is the compliance gap this issue exists to close.
 */
export function hasAcceptedTerms(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * Called after the login request is accepted, not before — a ticked box on a
 * request that failed should not suppress the next one.
 */
export function markTermsAccepted(): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(Math.floor(Date.now() / 1000)));
  } catch {
    // Storage unavailable. The server still has the record from this login;
    // the only consequence is the box showing again next time.
  }
}
